import {
  addExhibits,
  createChronology,
  getChronology,
  processExhibit,
} from 'modules/cases/medicalChronology/services/medicalChronologyService';
import { PROMOTION_QUERY_KEY } from 'modules/FileManagerV2/constants/medicalRecordPromotion';

/**
 * @param {unknown} response
 * @returns {object|null}
 */
const unwrapData = (response) => {
  if (response == null) return null;
  if (response?.data?.data !== undefined) return response.data.data;
  if (response?.data !== undefined) return response.data;
  return response;
};

/**
 * @param {unknown} response
 * @returns {object[]}
 */
const unwrapExhibitList = (response) => {
  const payload = unwrapData(response);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/**
 * Enroll an existing FileManager file into Medical Chronology (Sources-equivalent path).
 * Does not call promoteDocument, getExhibits, or medical-record-status (ADR-007 + FM UX).
 *
 * @param {{ caseId: string, fileId: string, fileSk?: string|null, queryClient: import('@tanstack/react-query').QueryClient }} params
 * @returns {Promise<{ status: 'enrolled', chronologyId: string }>}
 */
export const markFileAsMedicalRecordExhibit = async ({ caseId, fileId, fileSk, queryClient }) => {
  if (!caseId || !fileId || !queryClient) {
    throw new Error('caseId, fileId, and queryClient are required');
  }

  let chronology = unwrapData(await getChronology({ caseId }));
  if (!chronology?.chronology_id) {
    chronology = unwrapData(await createChronology({ caseId }));
  }

  const chronologyId = chronology?.chronology_id;
  if (!chronologyId) {
    throw new Error('Failed to resolve chronology for medical record enrollment');
  }

  const fileEntry = fileSk ? { file_id: fileId, file_sk: fileSk } : { file_id: fileId };
  const addResponse = await addExhibits({ chronologyId, files: [fileEntry] });
  const createdExhibits = unwrapExhibitList(addResponse).filter((exhibit) => exhibit?.exhibit_id);

  await Promise.all(createdExhibits.map((exhibit) => processExhibit({ chronologyId, exhibitId: exhibit.exhibit_id })));

  // Hide Mark without calling medical-record-status (no prefetch/refetch).
  queryClient.setQueryData([PROMOTION_QUERY_KEY, fileId], (prev) => ({
    ...(prev && typeof prev === 'object' ? prev : {}),
    promotionStatus: 'pending_promotion',
  }));

  await queryClient.invalidateQueries(['medchron-exhibits', chronologyId]);
  await queryClient.invalidateQueries(['medchron-record', caseId]);

  return { status: 'enrolled', chronologyId };
};
